import type { CartItem } from '@/types/cart';
import { getLocalizedText } from '@/utils/getLocalizedText';
import { useTranslation } from 'react-i18next';

interface OrderReviewItemProps {
  item: CartItem;
}

export function OrderReviewItem({ item }: OrderReviewItemProps) {
  const { i18n } = useTranslation('customer');
  const currentLanguage = (i18n.language === 'ar' ? 'ar' : 'en') as 'ar' | 'en';
  
  // Get localized item name based on current language
  const itemName = getLocalizedText(item, 'name', currentLanguage);
  const unitPriceEGP = (item.unitPrice / 100).toFixed(1);
  const lineTotalEGP = (item.lineTotal / 100).toFixed(1);

  return (
    <div 
      className="flex gap-3 py-4 border-b last:border-b-0"
      role="listitem"
      aria-label={`${itemName}, ${item.quantity} ${item.quantity === 1 ? 'item' : 'items'}`}
    >
      {/* Item Image */}
      {item.imageUrl && (
        <div className="flex-shrink-0">
          <img 
            src={item.imageUrl} 
            alt={itemName}
            className="w-16 h-16 object-cover rounded-md"
            loading="lazy"
          />
        </div>
      )}

      {/* Item Details */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            {/* Item Name - Bold, prominent */}
            <h4 className="font-bold text-base truncate">
              {itemName}
            </h4>
            {/* Quantity and Unit Price - Muted metadata */}
            <p className="text-xs text-muted-foreground mt-1">
              Qty: {item.quantity} × {unitPriceEGP} EGP
            </p>
            {/* Special Instructions - Italic, muted */}
            {item.specialInstructions && (
              <p className="text-xs text-muted-foreground italic mt-1">
                {item.specialInstructions}
              </p>
            )}
          </div>

          {/* Line Total - Bold, prominent, right-aligned */}
          <div className="text-right flex-shrink-0 rtl:text-left">
            <p className="font-bold text-lg">
              {lineTotalEGP} EGP
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
